import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '../../contexts/BookingContext';
import { generateTimeSlots, formatPrice } from '../../utils/timeUtils';
import { RichTextEditor } from '../RichTextEditor';
import type { GroupReservationRequest, FacilityReservationItem } from '../../types/reservation';
import type { AvailableTrainer } from '../../types/trainerAssociation';

type FacilitySelection = {
  facilityId: string;
  facilityName: string;
  date: string;
  startHour: number;
  endHour: number;
  numberOfUsers: number;
  payForAllUsers: boolean;
  pricePerHour?: number;
  trainerProfileId?: string | null;
};

type GroupBookingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialSelections?: FacilitySelection[];
  defaultDate: string;
};

export const GroupBookingModal: React.FC<GroupBookingModalProps> = ({
  isOpen,
  onClose,
  initialSelections = [],
  defaultDate,
}) => {
  const { t } = useTranslation();
  const { facilities, createGroupReservation, fetchFacilityBookings, facilityTimeSlots, fetchAvailableTrainers } = useBooking();

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Facility selections
  const [selections, setSelections] = useState<FacilitySelection[]>([]);

  // Available trainers per selection (keyed by index)
  const [availableTrainers, setAvailableTrainers] = useState<{ [index: number]: AvailableTrainer[] }>({});
  const [loadingTrainers, setLoadingTrainers] = useState<{ [index: number]: boolean }>({});

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeSlots = generateTimeSlots(6, 22);
  const today = new Date().toISOString().split('T')[0];

  // Track initialization and fetched combos
  const isInitializedRef = useRef(false);
  const fetchedCombosRef = useRef<Set<string>>(new Set());
  const prevIsOpenRef = useRef(isOpen);

  // Reset state when modal opens (only once per open)
  useEffect(() => {
    // Detect when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      isInitializedRef.current = false;
      fetchedCombosRef.current = new Set();
    }
    prevIsOpenRef.current = isOpen;

    if (isOpen && !isInitializedRef.current && facilities.length > 0) {
      isInitializedRef.current = true;

      setCustomerName('');
      setCustomerPhone('');
      setCustomerEmail('');
      setNotes('');
      setError(null);

      if (initialSelections.length > 0) {
        setSelections(initialSelections);
      } else {
        // Start with one empty selection
        const firstFacility = facilities[0];
        setSelections([{
          facilityId: firstFacility.id,
          facilityName: firstFacility.name,
          date: defaultDate,
          startHour: 10,
          endHour: 11,
          numberOfUsers: 1,
          payForAllUsers: false,
          pricePerHour: firstFacility.grossPricePerHour ?? firstFacility.pricePerHour,
        }]);
      }
    }
  }, [isOpen, initialSelections, facilities, defaultDate]);

  // Fetch bookings for new facility/date combinations only
  useEffect(() => {
    if (!isOpen || !isInitializedRef.current) return;

    const toFetch: Array<{ facilityId: string; date: string }> = [];

    selections.forEach(s => {
      const key = `${s.facilityId}:${s.date}`;
      if (!fetchedCombosRef.current.has(key)) {
        fetchedCombosRef.current.add(key);
        toFetch.push({ facilityId: s.facilityId, date: s.date });
      }
    });

    // Only fetch if there are new combinations
    toFetch.forEach(({ facilityId, date }) => {
      fetchFacilityBookings(facilityId, date).catch(console.error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selections]);

  // Fetch available trainers for a selection
  const fetchTrainersForSelection = useCallback(async (index: number, date: string, startHour: number, endHour: number) => {
    const timeSlots: string[] = [];
    for (let hour = startHour; hour < endHour; hour++) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    }

    if (timeSlots.length === 0) return;

    setLoadingTrainers(prev => ({ ...prev, [index]: true }));
    try {
      const trainers = await fetchAvailableTrainers(date, timeSlots);
      setAvailableTrainers(prev => ({ ...prev, [index]: trainers }));
    } catch (err) {
      console.error('Failed to fetch trainers:', err);
      setAvailableTrainers(prev => ({ ...prev, [index]: [] }));
    } finally {
      setLoadingTrainers(prev => ({ ...prev, [index]: false }));
    }
  }, [fetchAvailableTrainers]);

  // Track trainer fetches to avoid duplicates
  const trainerFetchKeyRef = useRef<{ [index: number]: string }>({});

  // Fetch trainers when date/time changes for each selection
  useEffect(() => {
    if (!isOpen || !isInitializedRef.current) return;

    selections.forEach((s, index) => {
      const key = `${s.date}:${s.startHour}:${s.endHour}`;
      if (trainerFetchKeyRef.current[index] !== key) {
        trainerFetchKeyRef.current[index] = key;
        fetchTrainersForSelection(index, s.date, s.startHour, s.endHour);
      }
    });
  }, [isOpen, selections, fetchTrainersForSelection]);

  if (!isOpen) return null;

  const addFacilitySelection = () => {
    if (facilities.length === 0) return;

    // Pick first facility not already selected; fall back to facilities[0]
    const usedIds = new Set(selections.map(s => s.facilityId));
    const nextFacility = facilities.find(f => !usedIds.has(f.id)) ?? facilities[0];

    // Inherit time/users from first selection if present, else hardcoded defaults
    const template = selections[0];
    const inherited = template
      ? {
          date: template.date,
          startHour: template.startHour,
          endHour: template.endHour,
          numberOfUsers: template.numberOfUsers,
          payForAllUsers: template.payForAllUsers,
        }
      : {
          date: defaultDate,
          startHour: 10,
          endHour: 11,
          numberOfUsers: 1,
          payForAllUsers: false,
        };

    setSelections(prev => [...prev, {
      facilityId: nextFacility.id,
      facilityName: nextFacility.name,
      pricePerHour: nextFacility.grossPricePerHour ?? nextFacility.pricePerHour,
      trainerProfileId: null,
      ...inherited,
    }]);
  };

  const removeFacilitySelection = (index: number) => {
    setSelections(prev => prev.filter((_, i) => i !== index));
  };

  const updateSelection = (index: number, updates: Partial<FacilitySelection>) => {
    setSelections(prev => prev.map((s, i) => {
      if (i !== index) return s;

      const updated = { ...s, ...updates };

      // Update facility name and price when facility changes
      if (updates.facilityId) {
        const facility = facilities.find(f => f.id === updates.facilityId);
        if (facility) {
          updated.facilityName = facility.name;
          updated.pricePerHour = facility.grossPricePerHour ?? facility.pricePerHour;
        }
      }

      // Reset trainer when date or time changes (trainer availability may differ)
      if (updates.date !== undefined || updates.startHour !== undefined || updates.endHour !== undefined) {
        updated.trainerProfileId = null;
      }

      return updated;
    }));
  };

  const isSlotAvailable = (facilityId: string, date: string, time: string): boolean => {
    const slots = facilityTimeSlots[facilityId]?.[date] || [];
    const status = slots.find(s => s.time === time);
    return (status?.isAvailable === true) && (status?.isBooked === false);
  };

  // Get trainer price for a selection
  const getTrainerPrice = (index: number, trainerProfileId: string | null | undefined): number => {
    if (!trainerProfileId) return 0;
    const trainer = availableTrainers[index]?.find((t: any) =>
      (t.trainerProfileId || t.id) === trainerProfileId
    ) as any;
    return trainer?.grossHourlyRate || trainer?.hourlyRate || trainer?.pricePerHour || 0;
  };

  // Calculate price for a single selection (facility + trainer)
  const calculateSelectionPrice = (selection: FacilitySelection, index: number): number => {
    const hours = selection.endHour - selection.startHour;
    const facilityPrice = (selection.pricePerHour || 0) * hours;
    const trainerPrice = getTrainerPrice(index, selection.trainerProfileId) * hours;
    return facilityPrice + trainerPrice;
  };

  const calculateTotalPrice = (): number => {
    return selections.reduce((total, s, index) => {
      return total + calculateSelectionPrice(s, index);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      setError(t('groupBooking.validation.customerNameRequired'));
      return;
    }

    if (selections.length === 0) {
      setError(t('groupBooking.validation.noFacilitiesSelected'));
      return;
    }

    // Validate all selections have valid time ranges
    for (let i = 0; i < selections.length; i++) {
      const s = selections[i];
      if (s.startHour >= s.endHour) {
        setError(t('groupBooking.validation.invalidTimeRange', { facility: s.facilityName }));
        return;
      }

      // Check if slots are available
      for (let hour = s.startHour; hour < s.endHour; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:00`;
        if (!isSlotAvailable(s.facilityId, s.date, time)) {
          setError(t('groupBooking.validation.slotNotAvailable', { facility: s.facilityName, time }));
          return;
        }
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const facilityReservations: FacilityReservationItem[] = selections.map(s => {
        const timeSlots: string[] = [];
        for (let hour = s.startHour; hour < s.endHour; hour++) {
          timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
        }

        return {
          facilityId: s.facilityId,
          date: s.date, // YYYY-MM-DD format
          timeSlots,
          numberOfUsers: s.numberOfUsers,
          payForAllUsers: s.payForAllUsers,
          trainerProfileId: s.trainerProfileId || null,
        };
      });

      const request: GroupReservationRequest = {
        facilityReservations,
        guestName: customerName.trim(),
        guestPhone: customerPhone.trim() || undefined,
        guestEmail: customerEmail.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      await createGroupReservation(request);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('groupBooking.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">
            {t('groupBooking.title')}
          </h3>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
            <p className="text-sm text-brand-warm">{error}</p>
          </div>
        )}

        {/* Customer Info Section */}
        <div className="mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              {t('groupBooking.customerName')} *
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder={t('groupBooking.customerNamePlaceholder')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                {t('groupBooking.phone')}
              </label>
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder={t('groupBooking.phonePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                {t('groupBooking.email')}
              </label>
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder={t('groupBooking.emailPlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Facility Reservations Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <label className="block text-sm font-medium text-white/70">
              {t('groupBooking.facilityReservations')}
            </label>
            <span className="text-xs text-white/60">
              {selections.length} {t('groupBooking.facilitiesSelected')}
            </span>
          </div>

          <div className="space-y-4">
            {selections.map((selection, index) => (
              <div key={index} className="p-4 bg-surface-card rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-brand-primary rounded-full flex items-center justify-center text-xs text-white font-bold">
                      {index + 1}
                    </span>
                    <select
                      value={selection.facilityId}
                      onChange={(e) => updateSelection(index, { facilityId: e.target.value })}
                      className="px-3 py-1.5 bg-surface-card border border-border-strong rounded-lg text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    >
                      {facilities.map(facility => (
                        <option key={facility.id} value={facility.id}>
                          {facility.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selections.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFacilitySelection(index)}
                      className="text-brand-warm-deep hover:text-brand-warm-deep transition-colors"
                      title={t('groupBooking.remove')}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-ink-secondary mb-1">{t('groupBooking.date')}</label>
                    <input
                      type="date"
                      value={selection.date}
                      min={today}
                      onChange={(e) => updateSelection(index, { date: e.target.value })}
                      className="w-full px-2 py-1.5 bg-surface-card border border-border-strong rounded-lg text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-xs text-ink-secondary mb-1">{t('groupBooking.startTime')}</label>
                      <select
                        value={selection.startHour}
                        onChange={(e) => updateSelection(index, { startHour: parseInt(e.target.value) })}
                        className="w-full px-2 py-1.5 bg-surface-card border border-border-strong rounded-lg text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      >
                        {timeSlots.map((time, i) => (
                          <option key={time} value={6 + i}>{time}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-ink-secondary mb-1">{t('groupBooking.endTime')}</label>
                      <select
                        value={selection.endHour}
                        onChange={(e) => updateSelection(index, { endHour: parseInt(e.target.value) })}
                        className="w-full px-2 py-1.5 bg-surface-card border border-border-strong rounded-lg text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                      >
                        {timeSlots.map((time, i) => (
                          <option key={time} value={6 + i}>{time}</option>
                        ))}
                        <option value={22}>22:00</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-ink-secondary mb-1">{t('groupBooking.numberOfUsers')}</label>
                    <input
                      type="number"
                      value={selection.numberOfUsers}
                      min={1}
                      onChange={(e) => updateSelection(index, { numberOfUsers: parseInt(e.target.value) || 1 })}
                      className="w-full px-2 py-1.5 bg-surface-card border border-border-strong rounded-lg text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-4">
                    <input
                      type="checkbox"
                      id={`payForAll-${index}`}
                      checked={selection.payForAllUsers}
                      onChange={(e) => updateSelection(index, { payForAllUsers: e.target.checked })}
                      className="w-4 h-4 rounded bg-surface-card border-border-strong text-brand-primary focus:ring-brand-primary"
                    />
                    <label htmlFor={`payForAll-${index}`} className="text-xs text-ink-secondary">
                      {t('groupBooking.payForAllUsers')}
                    </label>
                  </div>
                </div>

                {/* Trainer Selection */}
                <div className="mt-3">
                  <label className="block text-xs text-ink-secondary mb-1">{t('groupBooking.trainer')}</label>
                  <select
                    value={selection.trainerProfileId || ''}
                    onChange={(e) => updateSelection(index, { trainerProfileId: e.target.value || null })}
                    disabled={loadingTrainers[index]}
                    className="w-full px-2 py-1.5 bg-surface-card border border-border-strong rounded-lg text-ink-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
                  >
                    <option value="">{t('groupBooking.noTrainer')}</option>
                    {loadingTrainers[index] ? (
                      <option disabled>{t('groupBooking.loadingTrainers')}</option>
                    ) : (
                      availableTrainers[index]?.map((trainer: any) => {
                        const trainerPrice = trainer.grossHourlyRate || trainer.hourlyRate || trainer.pricePerHour;
                        return (
                          <option key={trainer.trainerProfileId || trainer.id} value={trainer.trainerProfileId || trainer.id}>
                            {trainer.trainerDisplayName || trainer.displayName || trainer.name}
                            {trainerPrice ? ` (+${formatPrice(trainerPrice)} PLN/h)` : ''}
                          </option>
                        );
                      })
                    )}
                  </select>
                  {!loadingTrainers[index] && availableTrainers[index]?.length === 0 && (
                    <p className="text-xs text-ink-muted mt-1">{t('groupBooking.noTrainersAvailable')}</p>
                  )}
                </div>

                <div className="mt-2 text-right text-sm text-ink-secondary">
                  {formatPrice(calculateSelectionPrice(selection, index))} PLN
                  {selection.trainerProfileId && getTrainerPrice(index, selection.trainerProfileId) > 0 && (
                    <span className="text-xs text-ink-muted ml-1">
                      ({t('groupBooking.includesTrainer')})
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addFacilitySelection}
            className="mt-3 w-full px-4 py-2 border-2 border-dashed border-white/30 text-white/80 rounded-lg hover:border-brand-primary-light hover:text-brand-primary-light transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('groupBooking.addFacility')}
          </button>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white/70 mb-2">
            {t('groupBooking.notes')} ({t('common.optional')})
          </label>
          <RichTextEditor
            value={notes}
            onChange={setNotes}
            placeholder={t('groupBooking.notesPlaceholder')}
            minRows={2}
          />
        </div>

        {/* Total Price */}
        <div className="mb-6 p-3 bg-surface-card rounded-lg flex justify-between items-center">
          <span className="text-ink-primary font-medium">{t('groupBooking.totalPrice')}</span>
          <span className="text-xl font-bold text-ink-primary">{formatPrice(calculateTotalPrice())} PLN</span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || selections.length === 0}
            className="flex-1 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? t('groupBooking.creating')
              : t('groupBooking.createButton')}
          </button>
        </div>
      </div>
    </div>
  );
};
